import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../../context/UserContext';
import { canAccessAdmin } from '../../utils/roleUtils';
import agencyAdminService from '../../services/agencyAdminService';
import { 
  Globe, Palette, Upload, Eye, Download, Settings, 
  AlertCircle, CheckCircle, RefreshCw, Save, Trash2,
  Image, FileText, Monitor, Smartphone, Tablet,
  Building2, Mail, Phone, MapPin, Link, Copy
} from 'lucide-react';
import '../../styles/modern-design-system.css';
import '../../styles/agency-admin.css';

const WhiteLabeling = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('branding');
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  
  const [branding, setBranding] = useState({
    logo: null,
    favicon: null,
    primaryColor: '#3B82F6',
    secondaryColor: '#8B5CF6',
    accentColor: '#10B981',
    fontFamily: 'Inter',
    customDomain: '',
    agencyName: '',
    tagline: '',
    contactInfo: {
      email: '',
      phone: '',
      address: '',
      website: ''
    }
  });

  const [domainConfig, setDomainConfig] = useState({
    customDomain: '',
    subdomain: '',
    sslEnabled: true,
    status: 'pending'
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  // Check if user has agency admin access
  const hasAgencyAdminAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  useEffect(() => {
    if (!hasAgencyAdminAccess()) {
      navigate('/agent/dashboard');
      return;
    }
    fetchBrandingData();
  }, [user, navigate]);

  const fetchBrandingData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Try to fetch from API first
      try {
        const response = await agencyAdminService.getBranding(user?.agencyId);
        if (response.success && response.data) {
          // Safely set branding with fallbacks for nested objects
          setBranding({
            logo: response.data.logo || null,
            favicon: response.data.favicon || null,
            primaryColor: response.data.primaryColor || '#3B82F6',
            secondaryColor: response.data.secondaryColor || '#8B5CF6',
            accentColor: response.data.accentColor || '#10B981',
            fontFamily: response.data.fontFamily || 'Inter',
            customDomain: response.data.customDomain || '',
            agencyName: response.data.agencyName || '',
            tagline: response.data.tagline || '',
            contactInfo: {
              email: response.data.contactInfo?.email || '',
              phone: response.data.contactInfo?.phone || '',
              address: response.data.contactInfo?.address || '',
              website: response.data.contactInfo?.website || ''
            }
          });
          
          if (response.data.domainConfig) {
            setDomainConfig({
              customDomain: response.data.domainConfig.customDomain || '',
              subdomain: response.data.domainConfig.subdomain || '',
              sslEnabled: response.data.domainConfig.sslEnabled !== undefined ? response.data.domainConfig.sslEnabled : true,
              status: response.data.domainConfig.status || 'pending'
            });
          }
        } else {
          throw new Error('Invalid API response');
        }
      } catch (apiError) {
        console.log('API not available, using mock data:', apiError);
        
        // Enhanced mock data
        setBranding({
          logo: null,
          favicon: null,
          primaryColor: '#3B82F6',
          secondaryColor: '#8B5CF6',
          accentColor: '#10B981',
          fontFamily: 'Inter',
          customDomain: 'myagency.parkpro.com',
          agencyName: 'Elite Travel Agency',
          tagline: 'Creating magical memories, one trip at a time',
          contactInfo: {
            email: 'info@elitetravel.com',
            phone: '+1 (555) 123-4567',
            address: '123 Travel Lane, Orlando, FL 32801',
            website: 'www.elitetravel.com'
          }
        });

        setDomainConfig({
          customDomain: 'myagency.parkpro.com',
          subdomain: 'myagency',
          sslEnabled: true,
          status: 'active'
        });
      }
    } catch (error) {
      console.error('Error fetching branding data:', error);
      // Ensure fallback data is set even on unexpected errors
      setBranding({
        logo: null,
        favicon: null,
        primaryColor: '#3B82F6',
        secondaryColor: '#8B5CF6',
        accentColor: '#10B981',
        fontFamily: 'Inter',
        customDomain: '',
        agencyName: '',
        tagline: '',
        contactInfo: {
          email: '',
          phone: '',
          address: '',
          website: ''
        }
      });
      setDomainConfig({
        customDomain: '',
        subdomain: '',
        sslEnabled: true,
        status: 'pending'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSaveBranding = async () => {
    try {
      setSaving(true);
      await agencyAdminService.updateBranding(user?.agencyId, branding);
      // Show success message
    } catch (error) {
      console.error('Error saving branding:', error);
      // Show error message
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const result = await agencyAdminService.uploadBrandAssets(user?.agencyId, formData);
      setBranding(prev => ({
        ...prev,
        [type]: result.url
      }));
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleDomainConfig = async () => {
    try {
      setSaving(true);
      await agencyAdminService.configureDomain(user?.agencyId, domainConfig);
      // Show success message
    } catch (error) {
      console.error('Error configuring domain:', error);
      // Show error message
    } finally {
      setSaving(false);
    }
  };

  if (!hasAgencyAdminAccess()) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access White Labeling.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
          <p className="text-gray-600">Loading branding settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <motion.main 
        className="flex-1 p-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">White Labeling</h1>
                <p className="mt-2 text-gray-600">Customize your agency's brand and domain</p>
              </div>
              <div className="flex items-center space-x-4">
                {/* Preview Mode Selector */}
                <div className="flex items-center space-x-2">
                  <Monitor className="w-4 h-4 text-gray-500" />
                  <select
                    value={previewMode}
                    onChange={(e) => setPreviewMode(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="desktop">Desktop</option>
                    <option value="tablet">Tablet</option>
                    <option value="mobile">Mobile</option>
                  </select>
                </div>
                
                {/* Refresh Button */}
                <button
                  onClick={() => fetchBrandingData(true)}
                  disabled={refreshing}
                  className="flex items-center px-3 py-2 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>

                {/* Save Button */}
                <button
                  onClick={handleSaveBranding}
                  disabled={saving}
                  className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="mr-2 w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px space-x-8">
                {[
                  { id: 'branding', label: 'Branding', icon: Palette },
                  { id: 'domain', label: 'Domain', icon: Globe },
                  { id: 'preview', label: 'Preview', icon: Eye },
                  { id: 'settings', label: 'Settings', icon: Settings }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      selectedTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="mr-2 w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Branding Tab */}
          {selectedTab === 'branding' && (
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Brand Identity */}
              <div className="settings-section agency-fade-in">
                <h3 className="settings-section-title">Brand Identity</h3>
                
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Logo Upload */}
                  <div className="space-y-4">
                    <div className="settings-field">
                      <label className="settings-field-label">Agency Logo</label>
                      <div className="p-6 text-center rounded-lg border-2 border-gray-300 border-dashed transition-colors brand-logo-preview hover:border-gray-400">
                        {branding?.logo ? (
                          <div className="space-y-2">
                            <img src={branding.logo} alt="Agency Logo" className="mx-auto w-auto h-20" />
                            <p className="text-sm text-gray-600">Logo uploaded successfully</p>
                            <button className="text-sm text-red-600 hover:text-red-800">Remove</button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Upload className="mx-auto w-8 h-8 text-gray-400" />
                            <p className="text-sm text-gray-600">Click to upload logo</p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                              className="hidden"
                              id="logo-upload"
                            />
                            <label htmlFor="logo-upload" className="text-sm text-blue-600 cursor-pointer hover:text-blue-800">
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">Favicon</label>
                      <div className="p-4 text-center rounded-lg border-2 border-gray-300 border-dashed transition-colors hover:border-gray-400">
                        {branding?.favicon ? (
                          <div className="space-y-2">
                            <img src={branding.favicon} alt="Favicon" className="mx-auto w-8 h-8" />
                            <p className="text-sm text-gray-600">Favicon uploaded</p>
                            <button className="text-sm text-red-600 hover:text-red-800">Remove</button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Image className="mx-auto w-6 h-6 text-gray-400" />
                            <p className="text-sm text-gray-600">Upload favicon (32x32px)</p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e.target.files[0], 'favicon')}
                              className="hidden"
                              id="favicon-upload"
                            />
                            <label htmlFor="favicon-upload" className="text-sm text-blue-600 cursor-pointer hover:text-blue-800">
                              Choose File
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Brand Information */}
                  <div className="space-y-4">
                    <div className="settings-field">
                      <label className="settings-field-label">Agency Name</label>
                      <input
                        type="text"
                        value={branding?.agencyName || ''}
                        onChange={(e) => setBranding(prev => ({ ...prev, agencyName: e.target.value }))}
                        className="settings-field-input"
                        placeholder="Enter agency name"
                      />
                    </div>

                    <div className="settings-field">
                      <label className="settings-field-label">Tagline</label>
                      <input
                        type="text"
                        value={branding?.tagline || ''}
                        onChange={(e) => setBranding(prev => ({ ...prev, tagline: e.target.value }))}
                        className="settings-field-input"
                        placeholder="Enter agency tagline"
                      />
                    </div>

                    <div className="settings-field">
                      <label className="settings-field-label">Font Family</label>
                      <select
                        value={branding?.fontFamily || 'Inter'}
                        onChange={(e) => setBranding(prev => ({ ...prev, fontFamily: e.target.value }))}
                        className="settings-field-input"
                      >
                        <option value="Inter">Inter</option>
                        <option value="Roboto">Roboto</option>
                        <option value="Open Sans">Open Sans</option>
                        <option value="Lato">Lato</option>
                        <option value="Poppins">Poppins</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Color Scheme */}
              <div className="settings-section agency-slide-up">
                <h3 className="settings-section-title">Color Scheme</h3>
                
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="settings-field">
                    <label className="settings-field-label">Primary Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={branding?.primaryColor || '#3B82F6'}
                        onChange={(e) => setBranding(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={branding?.primaryColor || '#3B82F6'}
                        onChange={(e) => setBranding(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="settings-field-input"
                      />
                    </div>
                  </div>

                  <div className="settings-field">
                    <label className="settings-field-label">Secondary Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={branding?.secondaryColor || '#8B5CF6'}
                        onChange={(e) => setBranding(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={branding?.secondaryColor || '#8B5CF6'}
                        onChange={(e) => setBranding(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        className="settings-field-input"
                      />
                    </div>
                  </div>

                  <div className="settings-field">
                    <label className="settings-field-label">Accent Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={branding?.accentColor || '#10B981'}
                        onChange={(e) => setBranding(prev => ({ ...prev, accentColor: e.target.value }))}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={branding?.accentColor || '#10B981'}
                        onChange={(e) => setBranding(prev => ({ ...prev, accentColor: e.target.value }))}
                        className="settings-field-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="settings-section agency-slide-up">
                <h3 className="settings-section-title">Contact Information</h3>
                
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="settings-field">
                    <label className="settings-field-label">Email</label>
                    <input
                      type="email"
                      value={branding?.contactInfo?.email || ''}
                      onChange={(e) => setBranding(prev => ({ 
                        ...prev, 
                        contactInfo: { ...(prev?.contactInfo || {}), email: e.target.value }
                      }))}
                      className="settings-field-input"
                      placeholder="info@youragency.com"
                    />
                  </div>

                  <div className="settings-field">
                    <label className="settings-field-label">Phone</label>
                    <input
                      type="tel"
                      value={branding?.contactInfo?.phone || ''}
                      onChange={(e) => setBranding(prev => ({ 
                        ...prev, 
                        contactInfo: { ...(prev?.contactInfo || {}), phone: e.target.value }
                      }))}
                      className="settings-field-input"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div className="settings-field">
                    <label className="settings-field-label">Address</label>
                    <input
                      type="text"
                      value={branding?.contactInfo?.address || ''}
                      onChange={(e) => setBranding(prev => ({ 
                        ...prev, 
                        contactInfo: { ...(prev?.contactInfo || {}), address: e.target.value }
                      }))}
                      className="settings-field-input"
                      placeholder="123 Main St, City, State 12345"
                    />
                  </div>

                  <div className="settings-field">
                    <label className="settings-field-label">Website</label>
                    <input
                      type="url"
                      value={branding?.contactInfo?.website || ''}
                      onChange={(e) => setBranding(prev => ({ 
                        ...prev, 
                        contactInfo: { ...(prev?.contactInfo || {}), website: e.target.value }
                      }))}
                      className="settings-field-input"
                      placeholder="www.youragency.com"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Domain Tab */}
          {selectedTab === 'domain' && (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="settings-section agency-fade-in">
                <h3 className="settings-section-title">Domain Configuration</h3>
                
                <div className="space-y-6">
                  <div className="settings-field">
                    <label className="settings-field-label">Custom Domain</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="text"
                        value={domainConfig.customDomain}
                        onChange={(e) => setDomainConfig(prev => ({ ...prev, customDomain: e.target.value }))}
                        className="settings-field-input"
                        placeholder="myagency.com"
                      />
                      <button
                        onClick={handleDomainConfig}
                        disabled={saving}
                        className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saving ? 'Configuring...' : 'Configure'}
                      </button>
                    </div>
                    <p className="settings-field-description">
                      Enter your custom domain (e.g., myagency.com) to replace the default subdomain.
                    </p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="mb-2 font-medium text-gray-900">Current Domain Status</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`status-indicator ${
                        domainConfig.status === 'active' 
                          ? 'status-active' 
                          : domainConfig.status === 'pending'
                          ? 'status-pending'
                          : 'status-error'
                      }`}>
                        {domainConfig.status}
                      </span>
                      <span className="text-sm text-gray-600">
                        {domainConfig.customDomain || `${domainConfig.subdomain}.parkpro.com`}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="settings-field">
                      <label className="settings-field-label">SSL Certificate</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={domainConfig.sslEnabled}
                          onChange={(e) => setDomainConfig(prev => ({ ...prev, sslEnabled: e.target.checked }))}
                          className="text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">Enable SSL (HTTPS)</span>
                      </div>
                    </div>

                    <div className="settings-field">
                      <label className="settings-field-label">Subdomain</label>
                      <input
                        type="text"
                        value={domainConfig.subdomain}
                        onChange={(e) => setDomainConfig(prev => ({ ...prev, subdomain: e.target.value }))}
                        className="settings-field-input"
                        placeholder="myagency"
                      />
                      <p className="settings-field-description">
                        Will be available at: {domainConfig.subdomain}.parkpro.com
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Preview Tab */}
          {selectedTab === 'preview' && (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="brand-preview agency-fade-in">
                <h3 className="mb-6 text-lg font-semibold text-gray-900">Brand Preview</h3>
                
                <div className="flex justify-center">
                  <div className={`border border-gray-300 rounded-lg overflow-hidden ${
                    previewMode === 'mobile' ? 'w-80' : 
                    previewMode === 'tablet' ? 'w-96' : 'w-full max-w-4xl'
                  }`}>
                    {/* Mock Website Preview */}
                    <div className="bg-white">
                      {/* Header */}
                      <div 
                        className="p-4 text-white"
                        style={{ backgroundColor: branding?.primaryColor || '#3B82F6' }}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            {branding?.logo && (
                              <img src={branding.logo} alt="Logo" className="w-auto h-8" />
                            )}
                            <h1 className="text-xl font-bold">{branding?.agencyName || 'Your Agency'}</h1>
                          </div>
                          <button className="px-4 py-2 bg-white bg-opacity-20 rounded-lg transition-colors hover:bg-opacity-30">
                            Get Started
                          </button>
                        </div>
                      </div>

                      {/* Hero Section */}
                      <div className="p-8 text-center">
                        <h2 className="mb-4 text-3xl font-bold text-gray-900">
                          {branding?.tagline || 'Creating magical memories, one trip at a time'}
                        </h2>
                        <p className="mb-6 text-gray-600">
                          Plan your perfect Disney vacation with our expert travel agents
                        </p>
                        <button 
                          className="px-6 py-3 font-medium text-white rounded-lg"
                          style={{ backgroundColor: branding?.accentColor || '#10B981' }}
                        >
                          Start Planning
                        </button>
                      </div>

                      {/* Features */}
                      <div className="p-8 bg-gray-50">
                        <h3 className="mb-8 text-2xl font-bold text-center text-gray-900">Why Choose Us?</h3>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                          {[
                            { title: 'Expert Planning', description: 'Professional Disney vacation planning' },
                            { title: 'Best Prices', description: 'Competitive rates and exclusive deals' },
                            { title: '24/7 Support', description: 'Round-the-clock customer service' }
                          ].map((feature, index) => (
                            <div key={index} className="text-center">
                              <div 
                                className="flex justify-center items-center mx-auto mb-4 w-12 h-12 text-white rounded-full"
                                style={{ backgroundColor: branding?.secondaryColor || '#8B5CF6' }}
                              >
                                <Building2 className="w-6 h-6" />
                              </div>
                              <h4 className="mb-2 font-semibold text-gray-900">{feature.title}</h4>
                              <p className="text-sm text-gray-600">{feature.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Footer */}
                      <div 
                        className="p-6 text-white"
                        style={{ backgroundColor: branding?.primaryColor || '#3B82F6' }}
                      >
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <div>
                            <h4 className="mb-2 font-semibold">Contact Info</h4>
                            <p className="text-sm opacity-90">{branding?.contactInfo?.email || ''}</p>
                            <p className="text-sm opacity-90">{branding?.contactInfo?.phone || ''}</p>
                          </div>
                          <div>
                            <h4 className="mb-2 font-semibold">Address</h4>
                            <p className="text-sm opacity-90">{branding?.contactInfo?.address || ''}</p>
                          </div>
                          <div>
                            <h4 className="mb-2 font-semibold">Website</h4>
                            <p className="text-sm opacity-90">{branding?.contactInfo?.website || ''}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Settings Tab */}
          {selectedTab === 'settings' && (
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="settings-section agency-slide-up">
                <h3 className="settings-section-title">White Label Settings</h3>
                
                <div className="space-y-6">
                  <div className="flex justify-between items-center p-4 rounded-lg border border-gray-200">
                    <div>
                      <h4 className="font-medium text-gray-900">Hide ParkPro Branding</h4>
                      <p className="text-sm text-gray-600">Remove ParkPro logos and references from your client-facing interface</p>
                    </div>
                    <input
                      type="checkbox"
                      className="text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex justify-between items-center p-4 rounded-lg border border-gray-200">
                    <div>
                      <h4 className="font-medium text-gray-900">Custom Email Templates</h4>
                      <p className="text-sm text-gray-600">Use your agency branding in automated emails</p>
                    </div>
                    <input
                      type="checkbox"
                      className="text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex justify-between items-center p-4 rounded-lg border border-gray-200">
                    <div>
                      <h4 className="font-medium text-gray-900">API Access</h4>
                      <p className="text-sm text-gray-600">Enable API access for custom integrations</p>
                    </div>
                    <input
                      type="checkbox"
                      className="text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex justify-between items-center p-4 rounded-lg border border-gray-200">
                    <div>
                      <h4 className="font-medium text-gray-900">Advanced Analytics</h4>
                      <p className="text-sm text-gray-600">Access detailed analytics and reporting features</p>
                    </div>
                    <input
                      type="checkbox"
                      className="text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
      </motion.main>
    </div>
  );
};

export default WhiteLabeling;